import { action, makeObservable, observable, runInAction } from 'mobx';

export type ToastType = 'success' | 'error';

export interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
  leaving: boolean;
}

/**
 * Minimal MobX-backed toast queue (replaces react-toastify). One bottom-center
 * pill per message; the store owns the queue, auto-dismiss and leave animation.
 */
class ToastStore {
  @observable toastItems: ToastItem[] = [];

  private _nextId = 1;

  // Show at most 4 pills; newest drops the oldest.
  private static readonly MAX_VISIBLE = 4;
  // How long a toast stays before it starts leaving.
  private static readonly DURATION = 2600;
  // Leave (fade-out) animation length; must match scss/toast.scss.
  private static readonly LEAVE = 200;

  constructor() {
    makeObservable(this);
  }

  @action.bound
  public success(message: string): void {
    this._push('success', message);
  }

  @action.bound
  public error(message: string): void {
    this._push('error', message);
  }

  @action.bound
  public dismiss(id: number): void {
    this._beginLeave(id);
  }

  @action
  private _push(type: ToastType, message: string): void {
    const id = this._nextId++;
    this.toastItems.push({ id, type, message, leaving: false });

    if (this.toastItems.length > ToastStore.MAX_VISIBLE) {
      this.toastItems = this.toastItems.slice(-ToastStore.MAX_VISIBLE);
    }

    window.setTimeout(() => this._beginLeave(id), ToastStore.DURATION);
  }

  @action
  private _beginLeave(id: number): void {
    const item = this.toastItems.find((t) => t.id === id);
    if (!item || item.leaving) {
      return;
    }
    item.leaving = true;

    window.setTimeout(
      () =>
        runInAction(() => {
          this.toastItems = this.toastItems.filter((t) => t.id !== id);
        }),
      ToastStore.LEAVE
    );
  }
}

export const toastStore = new ToastStore();
